import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './SchoolSignup.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const STUDENT_COUNT_OPTIONS = ['0-50', '50-100', '100+'];
const STUDENT_TYPES_OPTIONS = ['BAC', 'BEM', 'Middle', 'Primary'];
const EDUCATIONAL_LEVELS_OPTIONS = ['Primary', 'Middle', 'Secondary'];

const AccreditedSchoolSignup = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    schoolName: '',
    city: 'Algeria',
    directorName: '',
    phone: '',
    email: '',
    password: '',
    studentCountBucket: '',
    studentTypes: [],
    accreditationNumber: '',
    educationalLevels: [],
    teacherCount: '',
    administrativeContact: '',
    termsAccepted: false
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setError('');
  };

  const toggleArray = (name, val) => {
    setForm((prev) => {
      const arr = prev[name] || [];
      const next = arr.includes(val) ? arr.filter((t) => t !== val) : [...arr, val];
      return { ...prev, [name]: next };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.schoolName?.trim()) {
      setError('School name is required');
      return;
    }
    if (!form.directorName?.trim()) {
      setError('Director name is required');
      return;
    }
    if (!form.phone?.trim()) {
      setError('Phone number is required');
      return;
    }
    if (!form.email?.trim()) {
      setError('Email is required');
      return;
    }
    if (!form.password || form.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (!form.accreditationNumber?.trim()) {
      setError('Accreditation number is required for accredited schools');
      return;
    }
    if (!form.termsAccepted) {
      setError('You must agree to the platform terms');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/schools/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'accredited',
          schoolName: form.schoolName.trim(),
          city: form.city.trim() || 'Algeria',
          directorName: form.directorName.trim(),
          phone: form.phone.trim(),
          email: form.email.trim(),
          password: form.password,
          studentCountBucket: form.studentCountBucket || null,
          studentTypes: form.studentTypes.length ? form.studentTypes : null,
          accreditationNumber: form.accreditationNumber.trim(),
          educationalLevels: form.educationalLevels.length ? form.educationalLevels : null,
          teacherCount: form.teacherCount ? parseInt(form.teacherCount, 10) : null,
          administrativeContact: form.administrativeContact?.trim() || null,
          termsAccepted: true
        })
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(data.message || 'Registration failed');
        setLoading(false);
        return;
      }

      if (data.token && data.user) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify({ ...data.user, role: 'school_admin' }));
        navigate('/business/dashboard/accredited', { replace: true });
      } else {
        setError('Registration succeeded but invalid response');
      }
    } catch (err) {
      setError(err.message || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="school-signup-page accredited">
      <nav className="school-signup-nav">
        <Link to="/business" className="back-link">
          <span className="material-icons">arrow_back</span> Back to Business
        </Link>
        <Link to="/" className="logo-link">
          <span className="material-icons">school</span> Syllera
        </Link>
      </nav>

      <div className="school-signup-container">
        <h1 className="school-signup-title">Register Your Accredited School</h1>
        <p className="school-signup-subtitle">Complete institutional account for full school administration.</p>

        <form onSubmit={handleSubmit} className="school-signup-form">
          {error && (
            <div className="school-signup-error">
              <span className="material-icons">error_outline</span> {error}
            </div>
          )}

          <label className="school-signup-label">School Name *</label>
          <input
            type="text"
            name="schoolName"
            value={form.schoolName}
            onChange={handleChange}
            placeholder="Official school name"
            className="school-signup-input"
            required
          />

          <label className="school-signup-label">School Accreditation Number *</label>
          <input
            type="text"
            name="accreditationNumber"
            value={form.accreditationNumber}
            onChange={handleChange}
            placeholder="e.g. GOV-XXXXX"
            className="school-signup-input"
            required
          />

          <label className="school-signup-label">City</label>
          <input
            type="text"
            name="city"
            value={form.city}
            onChange={handleChange}
            placeholder="Algeria"
            className="school-signup-input"
          />

          <label className="school-signup-label">Director Name *</label>
          <input
            type="text"
            name="directorName"
            value={form.directorName}
            onChange={handleChange}
            placeholder="Full name"
            className="school-signup-input"
            required
          />

          <label className="school-signup-label">Phone Number *</label>
          <input
            type="tel"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            placeholder="+213 ..."
            className="school-signup-input"
            required
          />

          <label className="school-signup-label">Email *</label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            placeholder="school@example.com"
            className="school-signup-input"
            required
          />

          <label className="school-signup-label">Password * (min 8 characters)</label>
          <input
            type="password"
            name="password"
            value={form.password}
            onChange={handleChange}
            placeholder="••••••••"
            className="school-signup-input"
            minLength={8}
            required
          />

          <label className="school-signup-label">Educational Levels</label>
          <div className="school-signup-chips">
            {EDUCATIONAL_LEVELS_OPTIONS.map((opt) => (
              <button
                key={opt}
                type="button"
                className={`chip ${form.educationalLevels.includes(opt) ? 'active' : ''}`}
                onClick={() => toggleArray('educationalLevels', opt)}
              >
                {opt}
              </button>
            ))}
          </div>

          <label className="school-signup-label">Number of Teachers</label>
          <input
            type="number"
            name="teacherCount"
            value={form.teacherCount}
            onChange={handleChange}
            placeholder="0"
            min={0}
            className="school-signup-input"
          />

          <label className="school-signup-label">Administrative Contact (email or name)</label>
          <input
            type="text"
            name="administrativeContact"
            value={form.administrativeContact}
            onChange={handleChange}
            placeholder="Optional"
            className="school-signup-input"
          />

          <label className="school-signup-label">Number of Students</label>
          <select
            name="studentCountBucket"
            value={form.studentCountBucket}
            onChange={handleChange}
            className="school-signup-input"
          >
            <option value="">Select range</option>
            {STUDENT_COUNT_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>

          <label className="school-signup-label">Type of Students</label>
          <div className="school-signup-chips">
            {STUDENT_TYPES_OPTIONS.map((opt) => (
              <button
                key={opt}
                type="button"
                className={`chip ${form.studentTypes.includes(opt) ? 'active' : ''}`}
                onClick={() => toggleArray('studentTypes', opt)}
              >
                {opt}
              </button>
            ))}
          </div>

          <label className="school-signup-checkbox-label">
            <input
              type="checkbox"
              name="termsAccepted"
              checked={form.termsAccepted}
              onChange={handleChange}
            />
            <span>I agree to the platform terms</span>
          </label>

          <button type="submit" className="school-signup-submit accredited-btn" disabled={loading}>
            {loading ? 'Creating...' : 'Create Institutional Account'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AccreditedSchoolSignup;
